import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { ViewStyle } from 'react-native'
import { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

interface LayoutValues {
    x: number
    width: number
}

type LayoutMap = Partial<Record<DeliveryTypeCategory, LayoutValues>>

interface UseDeliveryTypeCategoryHighlightParams {
    activeCategory: DeliveryTypeCategory
    activeIndex: number
    layoutVersion: number
}

interface UseDeliveryTypeCategoryHighlightReturn {
    itemLayouts: LayoutMap
    setItemLayouts: Dispatch<SetStateAction<LayoutMap>>
    highlightStyle: ReturnType<typeof useAnimatedStyle<ViewStyle>>
}

export const useDeliveryTypeCategoryHighlight = ({
    activeCategory,
    activeIndex,
    layoutVersion,
}: UseDeliveryTypeCategoryHighlightParams): UseDeliveryTypeCategoryHighlightReturn => {
    const [itemLayouts, setItemLayouts] = useState<LayoutMap>({})
    const highlightX = useSharedValue(0)
    const highlightWidth = useSharedValue(0)
    const isResizingRef = useRef(false)
    const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        isResizingRef.current = true

        if (resizeTimerRef.current) {
            clearTimeout(resizeTimerRef.current)
        }

        resizeTimerRef.current = setTimeout(() => {
            isResizingRef.current = false
        }, 120)

        setItemLayouts({})
    }, [layoutVersion])

    useEffect(() => {
        const layout = itemLayouts[activeCategory]
        if (!layout || activeIndex < 0) return

        if (isResizingRef.current) {
            highlightX.value = layout.x
            highlightWidth.value = layout.width
            return
        }

        highlightX.value = withTiming(layout.x, {
            duration: 140,
            easing: Easing.out(Easing.cubic),
        })
        highlightWidth.value = withTiming(layout.width, {
            duration: 140,
            easing: Easing.out(Easing.cubic),
        })
    }, [activeCategory, activeIndex, highlightWidth, highlightX, itemLayouts])

    useEffect(
        () => () => {
            if (resizeTimerRef.current) {
                clearTimeout(resizeTimerRef.current)
            }
        },
        []
    )

    const highlightStyle = useAnimatedStyle<ViewStyle>(() => ({
        transform: [{ translateX: highlightX.value }],
        width: highlightWidth.value,
        opacity: highlightWidth.value > 0 ? 1 : 0,
    }))

    return { itemLayouts, setItemLayouts, highlightStyle }
}
